// RerunLogs.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Card,
    Badge,
} from "reactstrap";
import { FaHistory } from "react-icons/fa";
import { format } from "date-fns";
import ReactBsTable from "components/Tables/ReactBsTable";
import NotificationAlert from "react-notification-alert";
import { listRerunLogs } from "actions/ledger";
import ReactPlainTable from "components/Tables/ReactPlainTable";

// =============== COMPONENT ===============
const RerunLogs = ({ selectedMonth, selectedYear }) => {
    const notificationAlertRef = useRef(null);
    const [rerunLogs, setRerunLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // =============== NOTIFICATIONS ===============
    const notify = (type, message) => {
        if (notificationAlertRef.current) {
            notificationAlertRef.current.notificationAlert({
                place: "tr",
                message: <div>{message}</div>,
                type,
                icon: "tim-icons icon-bell-55",
                autoDismiss: 5,
            });
        }
    };

    // =============== FETCH RERUN LOGS ===============
    const fetchRerunLogs = async (page = 1) => {
        setIsLoading(true);
        try {
            const response = await listRerunLogs(page, selectedMonth,selectedYear);
            if (response?.code === 200 && response?.data) {
                const data = response.data;
                const ledgers = data.Ledger || data.data || [];
                
                // Map the rerun logs data
                const mappedLogs = ledgers.map(item => ({
                    id: item.ID || item.id,
                    companyName: item.companyName || "-",
                    openingBalance: item.openingBalance || 0,
                    revenue: item.revenue || 0,
                    adjustmentTotal: item.adjustmentTotal || 0,
                    advanceApplied: item.advanceApplied || 0,
                    closingBalance: item.closingBalance || 0,
                    netPayable: item.netPayable || 0,
                    currency: item.currency || "USD",
                    status: item.status || "RERUN",
                    postedBy: item.postedBy || "-",
                    postedAt: item.postedAt ? format(new Date(item.postedAt), "MMM dd, yyyy hh:mm a") : "-",
                    lockedAt: item.lockedAt || "-",
                    adjustment: item.adjustment || {},
                    invoiceInfo: item.invoiceInfo || {},
                    rerunReason: item.rerunReason || "-",
                    rerunBy: item.rerunBy || "-",
                    rerunAt: item.rerunAt ? format(new Date(item.rerunAt), "MMM dd, yyyy hh:mm a") : "-",
                    ledgerMonth: item.ledgerMonth || "-",
                }));
                
                setRerunLogs(mappedLogs);
                setTotalRecords(data.totalRecords || data.total_records || mappedLogs.length);
            } else {
                setRerunLogs([]);
                setTotalRecords(0);
                notify("warning", response?.message || "No rerun logs found");
            }
        } catch (error) {
            console.error("Error fetching rerun logs:", error);
            setRerunLogs([]);
            setTotalRecords(0);
            notify("danger", "Failed to fetch rerun logs");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRerunLogs(pageNum);
    }, []);

    // =============== HANDLERS ===============
    const handlePageChange = (page) => {
        setPageNum(page);
        fetchRerunLogs(page);
    };

    const handleSizePerPageChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setPageNum(1);
        fetchRerunLogs(1);
    };

    // =============== RENDER HELPERS ===============
    const getStatusBadge = (status) => {
        const statusMap = {
            ACTIVE: { color: "success", label: "Active" },
            INACTIVE: { color: "secondary", label: "Inactive" },
            PENDING: { color: "warning", label: "Pending" },
            APPROVED: { color: "success", label: "Approved" },
            REJECTED: { color: "danger", label: "Rejected" },
            RERUN: { color: "danger", label: "Rerun" },
            POSTED: { color: "info", label: "Posted" },
            PAID: { color: "success", label: "Paid" },
            LOCK: { color: "primary", label: "Locked" },
            OPEN: { color: "warning", label: "Open" },
            VOID: { color: "secondary", label: "Void" },
        };
        const s = statusMap[status] || statusMap.PENDING;
        return <Badge color={s.color}>{s.label}</Badge>;
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || isNaN(amount)) {
            return "$0.00";
        }
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
    };

    // =============== RERUN LOGS TABLE COLUMNS ===============
    const rerunColumns = [
         {
            dataField: "companyName",
            text: "Company Name",
            formatter: (cell) => cell || "-",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
      
        {
            dataField: "rerunReason",
            text: "Rerun Reason",
            formatter: (cell) => cell || "-",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
        {
            dataField: "revenue",
            text: "Revenue",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
        {
            dataField: "adjustmentTotal",
            text: "Adjustments",
            formatter: (cell) => formatCurrency(cell || 0),
            headerAlign: "right",
            align: "right",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
        {
            dataField: "advanceApplied",
            text: "Advance Applied",
            formatter: (cell) => formatCurrency(cell || 0),
            headerAlign: "right",
            align: "right",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
        {
            dataField: "netPayable",
            text: "Net Payable",
            formatter: (cell) => <span className="fw-bold text-primary">{formatCurrency(cell)}</span>,
            headerAlign: "right",
            align: "right",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
       
        {
            dataField: "rerunBy",
            text: "Rerun By",
            formatter: (cell) => cell || "-",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
        {
            dataField: "rerunAt",
            text: "Rerun At",
            formatter: (cell) => cell || "-",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
        {
            dataField: "postedBy",
            text: "Posted By",
            formatter: (cell) => cell || "-",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
        {
            dataField: "postedAt",
            text: "Posted At",
            formatter: (cell) => cell || "-",
            sort: true,
            headerStyle: { fontWeight: "bold" }
        },
         {
            dataField: "status",
            text: "Current Ledger Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
            headerStyle: { fontWeight: "bold" }
        }
    ];

    // =============== RENDER ===============
    return (
        <>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
          
                <>
                    <ReactPlainTable
                       
                        data={rerunLogs}
                        columns={rerunColumns}
                        keyField="id"
                        page={pageNum}
                        sizePerPage={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        showLoader={isLoading}
                        handleSizePerPageChange={handleSizePerPageChange}
                        bordered
                        hover
                        enablePageSizeManagement={false}
                        showAddbtn={false}
                        showColumnsBtn={false}
                        exportCSVBtnShow={false}
                    />
                </>
           
        </>
    );
};

export default RerunLogs;